import React from "react"
import ReactDOMServer from "react-dom/server"
import L from "leaflet"
import "leaflet.markercluster"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { GIS_MARKER_CLASS } from "@/features/gis/GisInteractivityContext.jsx"

const CLUSTER_OFF_ZOOM = 11
const ZOOM_STEP = 2
const LABEL_GAP_PX = 2

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)

const badgeHtml = (icon, [r, g, b]) =>
  ReactDOMServer.renderToStaticMarkup(
    <div className="gis-cluster-badge" style={{ borderColor: `rgb(${r}, ${g}, ${b})` }}>
      {React.createElement(icon, { style: { color: `rgb(${r}, ${g}, ${b})`, fontSize: 11 } })}
    </div>,
  )

// `label(cluster)` may name a group, `look(cluster)` may return another { rgb, icon } per group.
export function createGisCluster(
  map,
  { rgb, text = "black", clusterOffZoom = CLUSTER_OFF_ZOOM, maxClusterRadius, label = null, icon = null, look = null },
) {
  const badges = new Map()
  const badgeFor = (badgeIcon, badgeRgb) => {
    if (!badgeIcon) return ""
    if (!badges.has(badgeIcon)) badges.set(badgeIcon, new Map())
    const byRgb = badges.get(badgeIcon)
    const key = badgeRgb.join(",")
    if (!byRgb.has(key)) byRgb.set(key, badgeHtml(badgeIcon, badgeRgb))
    return byRgb.get(key)
  }
  const group = L.markerClusterGroup({
    chunkedLoading: true,
    spiderfyOnMaxZoom: true,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: false,
    maxClusterRadius: maxClusterRadius ?? ((zoom) => Math.max(60 - (zoom - 6) * 6, 20)),
    disableClusteringAtZoom: clusterOffZoom,
    iconCreateFunction: (cluster) => {
      const name = label?.(cluster)
      const style = look?.(cluster) ?? { rgb, icon }
      const [r, g, b] = style.rgb
      return L.divIcon({
        html: `${badgeFor(style.icon, style.rgb)}<div style="background: rgba(${r}, ${g}, ${b}, 0.8); color: ${text}; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 6px rgba(${r}, ${g}, ${b}, 0.25); font-weight: 500; font-size: 14px;">${cluster.getChildCount()}</div>${
          name ? `<div class="gis-cluster-label">${escapeHtml(name)}</div>` : ""
        }`,
        className: `custom-cluster-icon ${GIS_MARKER_CLASS}`,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
      })
    },
  })
  // Zoom in centred on the group: fitting all members jumped away when overlapping features lie far apart.
  group.on("clusterclick", (e) => {
    const zoom = map.getZoom()
    if (zoom >= clusterOffZoom || zoom >= map.getMaxZoom()) {
      e.layer.spiderfy()
      return
    }
    map.setView(e.layer.getLatLng(), Math.min(zoom + ZOOM_STEP, clusterOffZoom))
  })
  return group
}

const overlaps = (a, b) =>
  a.left < b.right + LABEL_GAP_PX &&
  b.left < a.right + LABEL_GAP_PX &&
  a.top < b.bottom + LABEL_GAP_PX &&
  b.top < a.bottom + LABEL_GAP_PX

// Hides labels that would cover another label or an obstacle (icons); the icon stays clickable.
export function declutterLabels(map, { labelSelector, obstacleSelector, cluster = null }) {
  let frame = 0
  const run = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      const root = map.getContainer()
      const placed = [...root.querySelectorAll(obstacleSelector)].map((el) => el.getBoundingClientRect())
      // Higher data-priority (e.g. port tonnage) keeps its label when two collide.
      const priority = (el) => Number(el.querySelector("[data-priority]")?.dataset.priority) || 0
      ;[...root.querySelectorAll(labelSelector)].sort((a, b) => priority(b) - priority(a)).forEach((el) => {
        el.style.visibility = ""
        const rect = el.getBoundingClientRect()
        if (placed.some((other) => overlaps(rect, other))) {
          el.style.visibility = "hidden"
          return
        }
        placed.push(rect)
      })
    })
  }
  map.on("zoomend moveend layeradd", run)
  // Groups re-form after zoomend; their animation end is when the icons have settled.
  cluster?.on("animationend", run)
  run()
  return () => {
    cancelAnimationFrame(frame)
    map.off("zoomend moveend layeradd", run)
    cluster?.off("animationend", run)
  }
}

// Round 24 px marker with a MUI icon, as used by the clustered GIS layers.
export function gisMarkerIcon(Icon, color, className) {
  return L.divIcon({
    html: ReactDOMServer.renderToStaticMarkup(
      <div
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          background: "white",
          border: `2px solid ${color}`,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Icon style={{ color, fontSize: 16 }} />
      </div>,
    ),
    className: `${className} ${GIS_MARKER_CLASS}`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })
}
